

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewItineraryDayLineItemModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        itinerary_day_id: '',
        day_number: '',
        title: '',
        description: '',
        image_url: '',
        inclusion: '',
        exclusion: '',
        guide_instructions: ''
    });

    const [itineraryDays, setItineraryDays] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchItineraryDays();
        }
    }, [isOpen]);

    const fetchItineraryDays = async () => {
        setLoadingData(true);
        try {
            const response = await axiosInstance.get('/itinerary-days/');
            setItineraryDays(response.data);
        } catch (error) {
            console.error('Error fetching itinerary days:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                itinerary_day_id: parseInt(formData.itinerary_day_id),
                day_number: formData.day_number ? parseInt(formData.day_number) : null,
            };

            await onSave(payload);
            onClose();
            // Reset form
            setFormData({
                itinerary_day_id: '', day_number: '', title: '', description: '',
                image_url: '', inclusion: '', exclusion: '', guide_instructions: ''
            });
        } catch (error) {
            console.error('Error saving Line Item:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Line Item',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '800px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Line Item</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        {loadingData ? (
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item>
                                    <CircularProgress />
                                </Grid>
                            </Grid>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" required>
                                            <InputLabel>Itinerary Day</InputLabel>
                                            <Select
                                                name="itinerary_day_id"
                                                value={formData.itinerary_day_id}
                                                onChange={handleInputChange}
                                                label="Itinerary Day"

                                            >
                                                <MenuItem value=""><em>Select Itinerary Day</em></MenuItem>
                                                {itineraryDays.map(day => (
                                                    <MenuItem key={day.id} value={day.id}>{day.name} - {day.title}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Day Number"
                                            name="day_number"
                                            value={formData.day_number}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Image URL"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Inclusion"
                                        name="inclusion"
                                        multiline
                                        rows={3}
                                        value={formData.inclusion}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Exclusion"
                                        name="exclusion"
                                        multiline
                                        rows={3}
                                        value={formData.exclusion}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Guide Instructions"
                                        name="guide_instructions"
                                        multiline
                                        rows={3}
                                        value={formData.guide_instructions}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                                    <MDBox display="flex" justifyContent="flex-end" mt={4} gap={1}>
                                        <MDButton variant="outlined" color="info" onClick={onClose}>
                                            Cancel
                                        </MDButton>
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            type="submit"
                                            disabled={isSaving}
                                        >
                                            {isSaving ? <CircularProgress size={20} color="white" /> : "Save"}
                                        </MDButton>
                                    </MDBox>
                                </form>
                            )}
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewItineraryDayLineItemModal;
